/**
 * Sets `--kb-offset: <Npx>` on the given element so its `bottom` /
 * `padding-bottom` can subtract the soft-keyboard height on iOS Safari.
 *
 * Background: `position: fixed; bottom: 0` is relative to the LAYOUT
 * viewport, which doesn't shrink when the soft keyboard opens. The element
 * ends up hidden behind the keyboard. `100dvh` helps for collapsing URL
 * bars but is inconsistent for keyboards across browsers. The
 * `visualViewport` API is the authoritative source.
 */
import { useEffect } from "react";

export function useKeyboardOffset(
  ref: React.RefObject<HTMLElement | null>,
  active: boolean = true,
) {
  useEffect(() => {
    if (!active) return;
    const vv = window.visualViewport;
    if (!vv) return; // Older browsers — gracefully degrade to plain dvh.

    const el = ref.current;
    if (!el) return;

    function update() {
      const layoutH = window.innerHeight;
      const visualBottomFromLayoutBottom = layoutH - (vv!.offsetTop + vv!.height);
      const offset = Math.max(0, visualBottomFromLayoutBottom);
      el!.style.setProperty("--kb-offset", `${offset}px`);
    }

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      el.style.removeProperty("--kb-offset");
    };
  }, [ref, active]);
}
